/**
 * The job state machine.
 *
 * This is the ONE piece of workflow Fanni refuses to make configurable, and
 * docs/ARCHITECTURE.md says so out loud. Sync correctness depends on transitions being
 * total, finite, and known to both the device and the server. A company that wants extra
 * statuses gets display-only sub-statuses layered on top of these; it does not get to add
 * a node to this graph.
 *
 * The server is authoritative. A device that has been offline may attempt a transition
 * from a state the server left hours ago — that is rejected, not forced, and the work the
 * technician did is preserved as an orphaned completion for a human to resolve.
 * See SYNC.md, Rule 5.
 */

export const JOB_STATES = [
  "draft",       // office is still writing it
  "scheduled",   // has a date, no technician
  "assigned",    // has a technician
  "en_route",    // he tapped "I'm driving"
  "on_site",     // he arrived
  "in_progress", // he started work
  "paused",      // waiting on a part, customer absent, prayer, lunch
  "completed",   // work done, signed
  "invoiced",    // invoice issued
  "closed",      // paid and closed
  "cancelled",   // dead
] as const;

export type JobState = (typeof JOB_STATES)[number];

/** Who is allowed to drive a transition. Dispatch belongs to the office; the job belongs to him. */
export type Actor = "office" | "technician" | "system";

type Transition = { to: JobState; by: Actor[] };

const GRAPH: Record<JobState, Transition[]> = {
  draft: [
    { to: "scheduled", by: ["office"] },
    { to: "assigned", by: ["office"] },
    { to: "cancelled", by: ["office"] },
  ],
  scheduled: [
    { to: "assigned", by: ["office"] },
    { to: "cancelled", by: ["office"] },
  ],
  assigned: [
    { to: "en_route", by: ["technician"] },
    { to: "on_site", by: ["technician"] }, // he forgot to tap en_route; do not punish him
    { to: "assigned", by: ["office"] },    // reassignment to a different technician
    { to: "scheduled", by: ["office"] },   // unassigned again
    { to: "cancelled", by: ["office"] },
  ],
  en_route: [
    { to: "on_site", by: ["technician"] },
    { to: "assigned", by: ["office"] },
    { to: "cancelled", by: ["office"] },
  ],
  on_site: [
    { to: "in_progress", by: ["technician"] },
    { to: "paused", by: ["technician"] },
    { to: "cancelled", by: ["office"] },
  ],
  in_progress: [
    { to: "paused", by: ["technician"] },
    { to: "completed", by: ["technician"] },
    { to: "cancelled", by: ["office"] },
  ],
  paused: [
    { to: "in_progress", by: ["technician"] },
    { to: "on_site", by: ["technician"] },
    { to: "cancelled", by: ["office"] },
  ],
  completed: [
    { to: "invoiced", by: ["office", "system"] },
    { to: "in_progress", by: ["office"] }, // reopened: he missed something
  ],
  invoiced: [
    { to: "closed", by: ["office", "system"] },
    { to: "completed", by: ["office"] }, // invoice voided
  ],
  closed: [],
  cancelled: [
    { to: "scheduled", by: ["office"] }, // un-cancel, e.g. resolving a completion conflict
  ],
};

/** States after which the technician's work is done and materials/photos are locked. */
export const TERMINAL_FOR_TECHNICIAN: readonly JobState[] = ["completed", "invoiced", "closed", "cancelled"];

/** States the office may still edit money on. */
export const BILLABLE_STATES: readonly JobState[] = ["completed", "invoiced"];

export function canTransition(from: JobState, to: JobState, by: Actor): boolean {
  return GRAPH[from].some((t) => t.to === to && t.by.includes(by));
}

export function allowedTransitions(from: JobState, by: Actor): JobState[] {
  return GRAPH[from].filter((t) => t.by.includes(by)).map((t) => t.to);
}

/**
 * Why a transition was refused — this string reaches a human, so it must survive
 * translation into a sentence a manager can act on.
 */
export type TransitionRefusal =
  | { ok: true }
  | { ok: false; reason: "unknown_state" | "illegal_transition" | "wrong_actor" };

export function checkTransition(from: JobState, to: JobState, by: Actor): TransitionRefusal {
  if (!GRAPH[from]) return { ok: false, reason: "unknown_state" };
  const legal = GRAPH[from].find((t) => t.to === to);
  if (!legal) return { ok: false, reason: "illegal_transition" };
  if (!legal.by.includes(by)) return { ok: false, reason: "wrong_actor" };
  return { ok: true };
}

/** Arabic labels. The technician reads these; they are not debug strings. */
export const JOB_STATE_AR: Record<JobState, string> = {
  draft: "مسودة",
  scheduled: "مجدولة",
  assigned: "مُسندة",
  en_route: "في الطريق",
  on_site: "في الموقع",
  in_progress: "قيد التنفيذ",
  paused: "متوقفة مؤقتاً",
  completed: "منجزة",
  invoiced: "مفوترة",
  closed: "مغلقة",
  cancelled: "ملغاة",
};
